import { i18n } from '@core/config/i18n.installer.js'
import router from '@core/config/router.installer.js'
import { AuthenticationError } from '@shared/errors/custom.error.js'
import { apiClient } from '@shared/services/http/http.service.js'
import ToastEventBus from 'primevue/toasteventbus'
import { normalizeBradleyMessages } from '../utils/bradley-message.util.js'

const SESSION_EXPIRED_REDIRECT_DELAY_MS = 4000

const API_BASE_URL
  = import.meta.env.VITE_BRADLEY_API
    || import.meta.env.VITE_API_URL
    || 'https://dev-bradley.dss360.app/api'
const FALLBACK_CLIENT_ID = '3f487497-c946-497a-9433-ec34270f4a3b'
const CONVERSATION_ID_STORAGE_KEY = 'bradley_conversation_id'

let cachedUserId = null
let isHandlingUnauthorized = false
let bradleyAccessTokenPromise = null
let bradleyRenewalEpoch = 0
let latestBradleyAccessToken = null

function showSessionExpiredToast() {
  ToastEventBus.emit('add', {
    severity: 'info',
    detail: i18n.global.t('newBradley.toasts.sessionExpiredMessage'),
    life: SESSION_EXPIRED_REDIRECT_DELAY_MS,
    group: 'bradley',
    closable: true,
  })
}

async function redirectToLoginAfterSessionExpired() {
  const authStore = await resolveAuthStore()
  if (authStore?.logout) {
    try {
      await authStore.logout()
    }
    catch (error) {
      console.warn('Failed to logout after Bradley session expired:', error)
    }
  }

  if (router.currentRoute.value.name !== 'AuthLogin') {
    await router.push({ name: 'AuthLogin' })
  }
}

function handleBradleyUnauthorized() {
  if (isHandlingUnauthorized) {
    throw new AuthenticationError('Authentication required')
  }

  isHandlingUnauthorized = true

  if (globalThis.window !== undefined) {
    showSessionExpiredToast()
    globalThis.setTimeout(() => {
      void redirectToLoginAfterSessionExpired()
    }, SESSION_EXPIRED_REDIRECT_DELAY_MS)
  }

  throw new AuthenticationError('Authentication required')
}

async function fetchGatekeeperValidation() {
  const response = await apiClient('gatekeeper').get('/token/validation')
  return response?.data ?? null
}

function hasValidatedUser(validation) {
  return Boolean(validation?.user?.id)
}

async function resolveAuthStore() {
  try {
    const { useAuthStore } = await import('@/modules/auth/stores/auth.store.js')
    return useAuthStore()
  }
  catch (error) {
    console.warn('Failed to load auth store for Bradley services:', error)
    return null
  }
}

function extractAccessTokenFromExchange(payload) {
  const token = payload?.data?.accessToken ?? payload?.accessToken ?? null
  return typeof token === 'string' && token.trim() ? token : null
}

function withBradleyAuthHeaders(accessToken, headers = {}) {
  if (!accessToken) {
    return { ...headers }
  }

  return {
    ...headers,
    Authorization: `Bearer ${accessToken}`,
  }
}

function rememberBradleyAccessToken(accessToken) {
  latestBradleyAccessToken = accessToken
  return accessToken
}

async function exchangeBradleyAccessToken(authStore) {
  bradleyRenewalEpoch += 1
  latestBradleyAccessToken = null

  if (!authStore?.refreshTokens) {
    handleBradleyUnauthorized()
  }

  authStore.accessToken = null

  try {
    const tokens = await authStore.refreshTokens()
    const accessToken = extractAccessTokenFromExchange(tokens)
    if (!accessToken) {
      handleBradleyUnauthorized()
    }
    authStore.accessToken = accessToken
    return rememberBradleyAccessToken(accessToken)
  }
  catch (error) {
    console.warn('Failed to refresh Bradley access token:', error)
    handleBradleyUnauthorized()
  }
}

async function resolveBradleyAccessTokenUncached() {
  const validation = await fetchGatekeeperValidation()
  const authStore = await resolveAuthStore()

  if (!hasValidatedUser(validation) || !authStore?.accessToken) {
    return exchangeBradleyAccessToken(authStore)
  }

  return rememberBradleyAccessToken(authStore.accessToken)
}

function resolveBradleyAccessToken() {
  if (!bradleyAccessTokenPromise) {
    bradleyAccessTokenPromise = resolveBradleyAccessTokenUncached().finally(() => {
      bradleyAccessTokenPromise = null
    })
  }

  return bradleyAccessTokenPromise
}

async function waitForBradleyAccessToken() {
  let epoch = bradleyRenewalEpoch
  let accessToken = await resolveBradleyAccessToken()

  for (;;) {
    const inFlight = bradleyAccessTokenPromise
    if (!inFlight && bradleyRenewalEpoch === epoch) {
      break
    }

    if (inFlight) {
      accessToken = await inFlight
    }
    else {
      accessToken = latestBradleyAccessToken ?? accessToken
    }
    epoch = bradleyRenewalEpoch
  }

  if (!accessToken) {
    handleBradleyUnauthorized()
  }

  return accessToken
}

export function resetBradleyAuthStateForTests() {
  cachedUserId = null
  isHandlingUnauthorized = false
  bradleyAccessTokenPromise = null
  bradleyRenewalEpoch = 0
  latestBradleyAccessToken = null
}

async function fetchWithCredentials(url, options = {}) {
  const accessToken = await waitForBradleyAccessToken()

  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: withBradleyAuthHeaders(accessToken, options.headers),
  })

  if (response.status === 401) {
    handleBradleyUnauthorized()
  }

  return response
}

export function getBradleyApiBaseUrl() {
  return API_BASE_URL
}

export async function bradleyAuthenticatedFetch(url, options = {}) {
  return fetchWithCredentials(url, options)
}

export async function parseBradleyServiceError(response, fallbackPrefix) {
  return parseErrorResponse(response, fallbackPrefix)
}

async function resolveBradleyUserId() {
  if (cachedUserId) {
    return cachedUserId
  }

  const authStore = await resolveAuthStore()
  const storeUserId = authStore?.userData?.id || authStore?.userData?._id || null

  if (storeUserId) {
    cachedUserId = String(storeUserId)
    return cachedUserId
  }

  const extracted = extractClientId() || FALLBACK_CLIENT_ID
  cachedUserId = extracted ? String(extracted) : null
  return cachedUserId
}

function getBrowserStorage() {
  if (typeof window === 'undefined') {
    return null
  }

  return window.localStorage ?? null
}

function extractClientId() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null
  }

  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index)
    if (!key) {
      continue
    }

    try {
      const rawValue = window.localStorage.getItem(key)
      if (rawValue) {
        try {
          const parsedValue = JSON.parse(rawValue)
          if (parsedValue && typeof parsedValue === 'object' && parsedValue.clientId) {
            return parsedValue.clientId
          }
        }
        catch {
          // Ignore JSON parse errors and try fallback below
        }
      }

      const idTokenMatch = key.match(/-idtoken-([a-f0-9-]+)-/i)
      if (idTokenMatch && idTokenMatch[1]) {
        return idTokenMatch[1]
      }
    }
    catch {
      continue
    }
  }

  return null
}

function readTrimmedString(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : ''
}

function extractBradleyErrorDescriptionFromPayload(payload) {
  if (!payload || typeof payload !== 'object') {
    return ''
  }

  return readTrimmedString(payload.error?.description)
    || readTrimmedString(payload.detail?.description)
    || readTrimmedString(payload.description)
    || readTrimmedString(payload.error?.detail?.description)
}

function extractBradleyErrorTypeFromPayload(payload) {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  if (typeof payload.error?.type === 'string') {
    return payload.error.type
  }
  if (typeof payload.detail?.type === 'string') {
    return payload.detail.type
  }
  if (typeof payload.type === 'string') {
    return payload.type
  }
  return null
}

function buildApiError(status, text, fallbackPrefix) {
  let errorType = null
  let description = null
  try {
    const json = JSON.parse(text)
    errorType = extractBradleyErrorTypeFromPayload(json)
    description = extractBradleyErrorDescriptionFromPayload(json)

    if (description) {
      const error = new Error(description)
      error.name = 'BradleyApiError'
      error.status = status
      error.description = description
      if (errorType) {
        error.type = errorType
      }
      return error
    }
  }
  catch {
    // not JSON or no detail
  }

  const error = new Error(`${fallbackPrefix} ${status} ${text}`)
  error.name = 'BradleyApiError'
  error.status = status
  if (errorType) {
    error.type = errorType
  }
  return error
}

export function getBradleyErrorDescription(error) {
  const fromProperty = readTrimmedString(error?.description)
  if (fromProperty) {
    return fromProperty
  }

  const message = readTrimmedString(error?.message)
  if (!message) {
    return ''
  }

  const jsonStart = message.indexOf('{')
  if (jsonStart !== -1) {
    try {
      const parsed = JSON.parse(message.slice(jsonStart))
      const fromPayload = extractBradleyErrorDescriptionFromPayload(parsed)
      if (fromPayload) {
        return fromPayload
      }
    }
    catch {
      // ignore malformed trailing JSON
    }
  }

  if (message.startsWith('Failed to ')) {
    return ''
  }

  return message
}

async function parseErrorResponse(response, fallbackPrefix) {
  const text = await response.text()
  return buildApiError(response.status, text, fallbackPrefix)
}

function postFormDataWithCredentials(url, formData) {
  return new Promise((resolve, reject) => {
    waitForBradleyAccessToken().then((accessToken) => {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', url)
      xhr.withCredentials = true
      xhr.setRequestHeader('Accept', 'application/json')
      xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`)
      xhr.onload = () => {
        if (xhr.status === 401) {
          handleBradleyUnauthorized()
        }

        resolve({
          status: xhr.status,
          responseText: xhr.responseText,
          getContentType: () => xhr.getResponseHeader('content-type'),
        })
      }
      xhr.onerror = () => reject(new Error('Failed to upload Bradley document: network error'))
      xhr.onabort = () => reject(new Error('Failed to upload Bradley document: aborted'))
      xhr.send(formData)
    }).catch(reject)
  })
}

export async function startBradleyConversation(title, options = {}) {
  const clientId = await resolveBradleyUserId()

  if (!clientId) {
    throw new Error('Client ID not found in localStorage')
  }

  const payload = {
    user_id: clientId,
    title: title || 'Conversation',
    use_genie: Boolean(options.useGenie),
  }

  const response = await fetchWithCredentials(`${API_BASE_URL}/conversation/start`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    throw await parseErrorResponse(response, 'Failed to start Bradley conversation')
  }

  const data = await response.json()
  if (data?.conversation_id) {
    storeConversationId(data.conversation_id)
  }
  return data
}

export async function sendBradleyMessage(conversationId, payload) {
  if (!conversationId) {
    throw new Error('conversationId is required to send a message')
  }

  const selectedMetadata = Array.isArray(payload?.selectedDocumentsMetadata)
    ? payload.selectedDocumentsMetadata
    : []
  const body = {
    content: payload?.content ?? '',
    use_genie: Boolean(payload?.useGenie),
    indexes_to_search: Array.isArray(payload?.indexesToSearch) ? payload.indexesToSearch : ['dss'],
    hierarchical_search: Boolean(payload?.hierarchicalSearch ?? true),
    k: typeof payload?.k === 'number' ? payload.k : 0,
    cosine_similarity_threshold: typeof payload?.cosineSimilarityThreshold === 'number'
      ? payload.cosineSimilarityThreshold
      : 0,
    selected_documents_metadata: selectedMetadata,
  }

  const response = await fetchWithCredentials(`${API_BASE_URL}/conversation/${conversationId}/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    throw await parseErrorResponse(response, 'Failed to send Bradley message')
  }

  return response.json()
}

function buildStreamRequestBody(payload = {}) {
  return {
    content: payload.content ?? '',
    use_genie: Boolean(payload?.useGenie),
    indexes_to_search: Array.isArray(payload.indexesToSearch) ? payload.indexesToSearch : ['dss'],
    hierarchical_search: Boolean(payload.hierarchicalSearch ?? true),
    k: typeof payload.k === 'number' ? payload.k : 0,
    cosine_similarity_threshold: typeof payload.cosineSimilarityThreshold === 'number'
      ? payload.cosineSimilarityThreshold
      : 0,
    selected_documents_metadata: Array.isArray(payload.selectedDocumentsMetadata)
      ? payload.selectedDocumentsMetadata
      : [],
  }
}

function dispatchStreamParsed(parsed, handlers) {
  const { onChunk, onFinal, onStep, onCitation } = handlers
  if (parsed?.type === 'chunk' && typeof parsed.content === 'string') {
    onChunk?.(parsed.content)
    return false
  }
  // A step: the backend sends the tool's identity, never a sentence. `status` is present
  // only when the step failed -- a step that worked is closed by the answer text arriving.
  if (parsed?.type === 'thought') {
    onStep?.({
      tool: parsed.tool ?? null,
      phase: parsed.phase ?? null,
      status: parsed.status ?? null,
      text: parsed.text ?? null,
    })
    return false
  }
  if (parsed?.type === 'citation' && Number.isInteger(parsed.marker)) {
    onCitation?.({
      marker: parsed.marker,
      doc_title: parsed.doc_title ?? '',
      share_point_url: parsed.share_point_url ?? '',
      source_index: parsed.source_index ?? '',
      ...(parsed.dko_document_type ? { dko_document_type: parsed.dko_document_type } : {}),
    })
    return false
  }
  if (parsed?.type === 'final') {
    onFinal?.(parsed.metadata ?? {})
    return true
  }
  // Fallback: stream broke and returned a flat JSON object in the legacy format
  if (parsed && !parsed.type && typeof parsed.response === 'string') {
    onFinal?.(parsed)
    return true
  }
  return false
}

function tryDispatchStreamLine(trimmed, handlers) {
  if (!trimmed) {
    return false
  }
  try {
    return dispatchStreamParsed(JSON.parse(trimmed), handlers)
  }
  catch {
    return false
  }
}

async function consumeBradleySseStream(reader, decoder, handlers) {
  let buffer = ''
  let receivedFinal = false

  while (true) {
    const { done, value } = await reader.read()
    if (done) {
      break
    }

    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() ?? ''

    for (const line of lines) {
      if (tryDispatchStreamLine(line.trim(), handlers)) {
        receivedFinal = true
      }
    }
  }

  const trailing = buffer.trim()
  if (trailing && tryDispatchStreamLine(trailing, handlers)) {
    receivedFinal = true
  }

  if (!receivedFinal) {
    handlers.onFinal?.({})
  }
}

export async function streamBradleyMessage(conversationId, payload, callbacks = {}) {
  if (!conversationId) {
    throw new Error('conversationId is required to stream a message')
  }

  const { onChunk, onFinal, onStep, onCitation, signal } = callbacks
  const body = buildStreamRequestBody(payload)

  const response = await fetchWithCredentials(
    `${API_BASE_URL}/conversation/${encodeURIComponent(conversationId)}/chat/stream`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body),
      signal,
    },
  )

  if (!response.ok) {
    throw await parseErrorResponse(response, 'Failed to stream Bradley message')
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()

  try {
    await consumeBradleySseStream(reader, decoder, { onChunk, onFinal, onStep, onCitation })
  }
  finally {
    reader.releaseLock()
  }
}

export async function fetchBradleyMessages(conversationId) {
  if (!conversationId) {
    throw new Error('conversationId is required to fetch messages')
  }

  const response = await fetchWithCredentials(`${API_BASE_URL}/conversation/${conversationId}/messages`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  })

  if (!response.ok) {
    throw await parseErrorResponse(response, 'Failed to fetch Bradley messages')
  }

  const data = await response.json()
  return normalizeBradleyMessages(data)
}

export function storeConversationId(conversationId) {
  const storage = getBrowserStorage()
  if (!storage || !conversationId) {
    return
  }

  storage.setItem(CONVERSATION_ID_STORAGE_KEY, conversationId)
}

export function getStoredConversationId() {
  const storage = getBrowserStorage()
  if (!storage) {
    return null
  }

  return storage.getItem(CONVERSATION_ID_STORAGE_KEY)
}

export function clearStoredConversationId() {
  const storage = getBrowserStorage()
  if (!storage) {
    return
  }

  storage.removeItem(CONVERSATION_ID_STORAGE_KEY)
}

function buildBradleyUploadFormData(payload, requiredErrorMessage) {
  const rawFile = payload?.file
  if (rawFile == null) {
    throw new Error(requiredErrorMessage)
  }

  const trimmedName = typeof payload?.file_name === 'string' ? payload.file_name.trim() : ''
  const fileName = trimmedName || (rawFile instanceof File && rawFile.name) || 'upload'

  const formData = new FormData()
  formData.append('file', rawFile, fileName)
  formData.append('file_name', fileName)

  return formData
}

async function postBradleyUpload(url, formData, failurePrefix) {
  const { status, responseText, getContentType } = await postFormDataWithCredentials(url, formData)

  if (status < 200 || status >= 300) {
    throw buildApiError(status, responseText, failurePrefix)
  }

  const contentType = getContentType() || ''
  if (contentType.includes('application/json')) {
    return JSON.parse(responseText)
  }
  return responseText
}

export async function uploadBradleyDocument(conversationId, payload) {
  if (!conversationId) {
    throw new Error('conversationId is required to upload documents')
  }

  const formData = buildBradleyUploadFormData(payload, 'file is required to upload documents')
  const url = `${API_BASE_URL}/conversation/${conversationId}/uploaded-documents`
  return postBradleyUpload(url, formData, 'Failed to upload Bradley document')
}

export async function uploadBradleyBlobStorageFile(conversationId, payload) {
  if (!conversationId) {
    throw new Error('conversationId is required to upload blob storage files')
  }

  const formData = buildBradleyUploadFormData(payload, 'file is required to upload blob storage files')
  const url = `${API_BASE_URL}/conversation/${conversationId}/blob-storage-file/upload`
  return postBradleyUpload(url, formData, 'Failed to upload Bradley blob storage file')
}

export const BRADLEY_VOICE_NOTE_TIMEOUT_MS = 120_000
export const BRADLEY_VOICE_NOTE_MAX_BYTES = 25 * 1024 * 1024

function appendVoiceNoteDuration(formData, durationMs) {
  const parsed = Number(durationMs)
  if (!Number.isFinite(parsed)) {
    return
  }

  formData.append('duration_ms', String(Math.round(parsed)))
}

function createVoiceNoteAbort(timeoutMs, externalSignal) {
  const controller = new AbortController()
  let timedOut = false

  const timeoutId = setTimeout(() => {
    timedOut = true
    controller.abort()
  }, timeoutMs)

  const abortFromExternal = () => {
    controller.abort()
  }

  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort()
    }
    else {
      externalSignal.addEventListener('abort', abortFromExternal, { once: true })
    }
  }

  return {
    signal: controller.signal,
    didTimeOut: () => timedOut,
    cleanup() {
      clearTimeout(timeoutId)
      externalSignal?.removeEventListener?.('abort', abortFromExternal)
    },
  }
}

function buildVoiceNoteTimeoutError() {
  const error = new Error('Failed to transcribe Bradley voice note')
  error.name = 'BradleyApiError'
  error.status = 503
  return error
}

export async function transcribeBradleyVoiceNote(conversationId, payload, options = {}) {
  if (!conversationId) {
    throw new Error('conversationId is required to transcribe a voice note')
  }

  const rawFile = payload?.file
  if (rawFile == null) {
    throw new Error('file is required to transcribe a voice note')
  }

  const trimmedName = typeof payload?.file_name === 'string' ? payload.file_name.trim() : ''
  const fileName = trimmedName || (rawFile instanceof File && rawFile.name) || ''
  if (!fileName) {
    throw new Error('file_name is required to transcribe a voice note')
  }

  const formData = new FormData()
  formData.append('file', rawFile, fileName)
  formData.append('file_name', fileName)
  appendVoiceNoteDuration(formData, payload?.duration_ms ?? payload?.durationMs)

  const timeoutMs = Number.isFinite(options.timeoutMs)
    ? options.timeoutMs
    : BRADLEY_VOICE_NOTE_TIMEOUT_MS
  const abort = createVoiceNoteAbort(timeoutMs, options.signal)

  try {
    const response = await fetchWithCredentials(
      `${API_BASE_URL}/conversation/${encodeURIComponent(conversationId)}/voice-note`,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: formData,
        signal: abort.signal,
      },
    )

    if (!response.ok) {
      throw await parseErrorResponse(response, 'Failed to transcribe Bradley voice note')
    }

    const data = await response.json()
    return {
      ...data,
      text: typeof data?.text === 'string' ? data.text : '',
    }
  }
  catch (error) {
    if (error?.name === 'AbortError' && abort.didTimeOut() && !options.signal?.aborted) {
      throw buildVoiceNoteTimeoutError()
    }
    throw error
  }
  finally {
    abort.cleanup()
  }
}

export const BRADLEY_BLOB_TRANSFORMATION_POLL_INTERVAL_MS = 10_000
export const BRADLEY_BLOB_TRANSFORMATION_REQUESTED_TIMEOUT_MS = 5 * 60_000

function normalizeBradleyBlobTransformationFiles(payload) {
  if (!payload || typeof payload !== 'object') {
    return []
  }

  const candidates = [
    payload.files,
    payload.retorno?.files,
    payload.data?.files,
    payload.result?.files,
  ]

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate.filter(Boolean)
    }
  }

  return []
}

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export async function fetchBradleyBlobTransformationStatus(conversationId, fileIds) {
  if (!conversationId) {
    throw new Error('conversationId is required to fetch blob transformation status')
  }

  const ids = (Array.isArray(fileIds) ? fileIds : [fileIds])
    .filter(id => id != null && String(id).trim())
    .map(id => String(id).trim())

  if (!ids.length) {
    throw new Error('file_ids is required to fetch blob transformation status')
  }

  const response = await fetchWithCredentials(
    `${API_BASE_URL}/conversation/${encodeURIComponent(conversationId)}/blob-storage-file/transformation-status`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ file_ids: ids }),
    },
  )

  if (!response.ok) {
    throw await parseErrorResponse(response, 'Failed to fetch Bradley blob transformation status')
  }

  const data = await response.json()
  return {
    ...data,
    files: normalizeBradleyBlobTransformationFiles(data),
  }
}

export async function waitForBradleyBlobTransformation({
  conversationId,
  fileIds,
  intervalMs = BRADLEY_BLOB_TRANSFORMATION_POLL_INTERVAL_MS,
  requestedTimeoutMs = BRADLEY_BLOB_TRANSFORMATION_REQUESTED_TIMEOUT_MS,
} = {}) {
  const ids = (Array.isArray(fileIds) ? fileIds : [fileIds])
    .filter(id => id != null && String(id).trim())
    .map(id => String(id).trim())

  if (!conversationId) {
    throw new Error('conversationId is required to wait for blob transformation')
  }
  if (!ids.length) {
    throw new Error('file_ids is required to wait for blob transformation')
  }

  const startedAt = Date.now()

  while (true) {
    const statusPayload = await fetchBradleyBlobTransformationStatus(conversationId, ids)
    const files = Array.isArray(statusPayload.files) ? statusPayload.files : []
    const byId = new Map(
      files
        .filter(file => file?.file_id != null)
        .map(file => [String(file.file_id), file]),
    )

    const resolved = ids.map((fileId) => {
      const entry = byId.get(fileId)
      return entry || { file_id: fileId, transformation_status: 'requested' }
    })

    const allTerminal = resolved.every((file) => {
      const status = file.transformation_status
      return status === 'ready' || status === 'failed'
    })

    if (allTerminal) {
      return resolved
    }

    const hasRequested = resolved.some(file => file.transformation_status === 'requested')
    if (hasRequested && Date.now() - startedAt >= requestedTimeoutMs) {
      return resolved.map((file) => {
        if (file.transformation_status === 'requested') {
          return {
            ...file,
            transformation_status: 'failed',
            timed_out: true,
          }
        }
        return file
      })
    }

    await sleep(intervalMs)
  }
}

function parseContentDispositionFileName(contentDisposition) {
  if (!contentDisposition || typeof contentDisposition !== 'string') {
    return null
  }

  const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
  const rawName = utfMatch?.[1]
    ?? contentDisposition.match(/filename="([^"]+)"/i)?.[1]
    ?? contentDisposition.match(/filename=([^;]+)/i)?.[1]
  if (!rawName) {
    return null
  }

  try {
    return decodeURIComponent(rawName.replace(/^.*[/\\]/, '').trim()) || null
  }
  catch {
    return rawName.replace(/^.*[/\\]/, '').trim() || null
  }
}

export async function downloadBradleyConversationFile(conversationId, fileId) {
  if (!conversationId) {
    throw new Error('conversationId is required to download a conversation file')
  }
  if (!fileId) {
    throw new Error('fileId is required to download a conversation file')
  }

  const response = await fetchWithCredentials(
    `${API_BASE_URL}/conversation/${encodeURIComponent(conversationId)}/blob-storage-files/${encodeURIComponent(fileId)}?download=true`,
    { method: 'GET' },
  )

  if (!response.ok) {
    throw await parseErrorResponse(response, 'Failed to download Bradley conversation file')
  }

  const blob = await response.blob()
  const fileName = parseContentDispositionFileName(response.headers?.get?.('content-disposition'))

  return { blob, fileName }
}

export async function fetchBradleyUploadedDocuments(conversationId) {
  if (!conversationId) {
    throw new Error('conversationId is required to fetch uploaded documents')
  }

  const response = await fetchWithCredentials(`${API_BASE_URL}/conversation/${conversationId}/uploaded-documents`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  })

  if (!response.ok) {
    throw await parseErrorResponse(response, 'Failed to fetch Bradley uploaded documents')
  }

  const data = await response.json()
  if (!Array.isArray(data)) {
    return []
  }

  return data
}

export async function fetchBradleyConversationsByUser() {
  const clientId = await resolveBradleyUserId()

  if (!clientId) {
    throw new Error('User ID not available to fetch conversations')
  }

  const response = await fetchWithCredentials(`${API_BASE_URL}/conversation/by_user/${encodeURIComponent(clientId)}`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  })

  if (!response.ok) {
    throw await parseErrorResponse(response, 'Failed to fetch Bradley conversations')
  }

  const data = await response.json()
  if (!Array.isArray(data)) {
    return []
  }

  return data
}

export async function deleteBradleyConversation(conversationId) {
  if (!conversationId) {
    throw new Error('conversationId is required to delete a conversation')
  }

  const response = await fetchWithCredentials(`${API_BASE_URL}/conversation/${encodeURIComponent(conversationId)}`, {
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
    },
  })

  if (!response.ok) {
    throw await parseErrorResponse(response, 'Failed to delete Bradley conversation')
  }

  if (response.status === 204) {
    return null
  }
  return response.json().catch(() => null)
}

export async function clearAllBradleyConversations() {
  const response = await fetchWithCredentials(`${API_BASE_URL}/conversation/user/clear_all`, {
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
    },
  })

  if (!response.ok) {
    throw await parseErrorResponse(response, 'Failed to clear all Bradley conversations')
  }

  return response.json().catch(() => ({ deleted: true, count: 0 }))
}

export async function renameBradleyConversationTitle(conversationId, title) {
  if (!conversationId) {
    throw new Error('conversationId is required to rename a conversation')
  }

  const sanitizedTitle = typeof title === 'string' ? title.trim() : ''
  if (!sanitizedTitle) {
    throw new Error('title is required to rename a conversation')
  }

  const endpoint = `${API_BASE_URL}/conversation/${encodeURIComponent(conversationId)}/title`
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
  const body = JSON.stringify({ title: sanitizedTitle })

  const patchResponse = await fetchWithCredentials(endpoint, {
    method: 'PATCH',
    headers,
    body,
  })

  if (patchResponse.ok) {
    return patchResponse.status === 204 ? null : patchResponse.json().catch(() => null)
  }

  if (patchResponse.status === 405 || patchResponse.status === 404) {
    const putResponse = await fetchWithCredentials(endpoint, {
      method: 'PUT',
      headers,
      body,
    })

    if (!putResponse.ok) {
      throw await parseErrorResponse(putResponse, 'Failed to rename Bradley conversation')
    }

    return putResponse.status === 204 ? null : putResponse.json().catch(() => null)
  }

  throw await parseErrorResponse(patchResponse, 'Failed to rename Bradley conversation')
}

export async function sendBradleyFeedback(conversationId, feedbackData) {
  if (!conversationId) {
    throw new Error('conversationId is required to send feedback')
  }

  const response = await fetchWithCredentials(`${API_BASE_URL}/conversation/${encodeURIComponent(conversationId)}/feedback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(feedbackData),
  })

  if (!response.ok) {
    throw await parseErrorResponse(response, 'Failed to send feedback')
  }

  return response.json()
}

export async function sendBradleyReport(conversationId, reportData) {
  if (!conversationId) {
    throw new Error('conversationId is required to send report')
  }

  const response = await fetchWithCredentials(`${API_BASE_URL}/conversation/${encodeURIComponent(conversationId)}/report`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(reportData),
  })

  if (!response.ok) {
    throw await parseErrorResponse(response, 'Failed to send report')
  }

  return response.json()
}
